import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of, throwError} from 'rxjs';
import { withLatestFrom, switchMap, map, catchError } from 'rxjs/operators';

import * as clientActions from './client.actions';
import * as tasksActions from '../../../tasks/store/tasks.actions';

import {
    SessionSelectors,
    ClientSelectors,
    ClientService,
    CreaditAutorisationService,
    CreaditCodeService,
    VederlagService,
    KunderAdrService,
} from '@store';

import { Authorization, ClientCode, PatchObject, Client, Vederlag, KunderAdr } from '@store/models';
import { AlertService } from '../../../core/alerts/alert.service';

@Injectable()
export class ClientEffects {

    LoadClientData$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.loadClientData),
        withLatestFrom(this.sessionSelector.recentClientKid$),
        switchMap(([, kid]) => {
            if (!kid) { return of(clientActions.loadClientDataFailed()); }
            return this.clientService.getClientDetails(kid).pipe(
                map(clientDetails => clientActions.loadClientDataSuccess({ clientDetails })),
                catchError(() => of(clientActions.loadClientDataFailed()))
            );
        }
        )
    ));

    LoadClientDataSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.loadClientDataSuccess),
        map(() => tasksActions.LoadClientTasks())
    ));

    UpdateClientData$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateClientData),
        withLatestFrom(this.clientSelector.client$, this.clientSelector.clientKid$),
        switchMap(([action, oldValues, kid]) => {
            const newValues = action.partialClient;
            const patchValues: PatchObject[] = [];
            Object.keys(newValues).forEach(key => {
                const newValue = newValues[key];
                const oldValue = oldValues[key];
                if (newValue !== oldValue
                    && (!(!newValue && !oldValue)
                    || newValue === 0 && oldValue === null
                    || typeof newValue === 'boolean' )) {
                    patchValues.push({value: newValue, path: '/' + key, op: 'replace'});
                }
            });
            return this.clientService.updateClient(patchValues, kid).pipe(
                map((partialClient: Client) => clientActions.updateClientDataSuccess({ partialClient })),
                catchError((err) => {
                    if (err.status === 400) {
                        return of(clientActions.updateClientDataFailed(err));
                    }
                    return throwError(err);
                }
                )
            );
        })
    ));

    UpdateClientDataFailed$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateClientDataFailed),
        map(action => {
            this.alert.createAlert(action.error, 'danger');
        })
    ), { dispatch: false });

    ny469$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.ny469),
        switchMap(action => this.clientService.ny469(action.kid).pipe(
            map(partialClient => clientActions.updateClientDataSuccess({partialClient})),
            catchError((err) => {
                if (err.status === 400) {
                    return of(clientActions.updateClientDataFailed(err));
                }
                return throwError(err);
            })
        ))
    ));

    UpdateClientInfo$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateClientInfo),
        withLatestFrom(this.clientSelector.client$, this.clientSelector.clientKid$),
        switchMap(([action, oldValues, kid]) => {
            const patchValues: PatchObject[] = [];
            if (oldValues.kundeinformationsblad !== action.info) {
                patchValues.push({value: action.info ? 1 : 0, path: '/kundeinformationsblad', op: 'replace'});
            }
            return this.clientService.updateClient(patchValues, kid).pipe(
                map((partialClient: Client) => clientActions.updateClientDataSuccess({ partialClient })),
                catchError((err) => throwError(err))
            );
        })
    ));

    UpdateCommunicationData$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateCommunicationData),
        withLatestFrom(this.clientSelector.client$),
        switchMap(([action, client]) => {
            action.communicationObj.map(option => {
                option.kid = client.kid;
                option.kontor = client.kontor;
            });
            return this.clientService.updateCommunication(action.communicationObj, client.kid.toString()).pipe(
                map((communicationObj) => clientActions.updateCommunicationDataSuccess({ communicationObj })),
                catchError((err) => throwError(err))
            );
        })
    ));

    CreateCode$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.createCode),
        withLatestFrom(
            this.clientSelector.clientKid$),
        switchMap(([action, kid]) => {
            const newCode: ClientCode = {
                ...action.code,
                clientId: kid
            };
            return this.codeService.createCode(newCode).pipe(
                map(code => clientActions.createCodeSuccess({ code }))
            );
        })
    ));

    UpdateCode$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateCode),
        withLatestFrom(this.clientSelector.clientCodes$),
        switchMap(([action, codes]) => {
            const oldValues = codes.find(c => c.id === action.code.id);
            const patchValues: PatchObject[] = [];
            Object.keys(action.code).forEach(key => {
                if (action.code[key] !== oldValues[key] && !(!action.code[key] && !oldValues[key])) {
                    patchValues.push({value: action.code[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.codeService.updateCode(patchValues, action.code.id).pipe(
                map(() => clientActions.updateCodeSuccess({ code: action.code })),
                catchError((err) => throwError(err))
            );
        })
    ));

    DeleteCode$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.deleteCode),
        switchMap((action) => this.clientService.deleteCode(action.id).pipe(
            map(() => clientActions.deleteCodeSuccess({ id: action.id })),
            catchError((err) => throwError(err))
        ))
    ));

    CreateAutorisation$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.createAutorisation),
        withLatestFrom(
            this.clientSelector.client$),
        switchMap(([action, client]) => {
            const newAuthorization: Authorization = {
                ...action.authorization,
                kid: client.kid,
                kontor: client.kontor
            };
            return this.autorisationService.createAuthorization(newAuthorization).pipe(
                map(authorization => clientActions.createAutorisationSuccess({ authorization }))
            );
        })
    ));

    UpdateAutorisation$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateAutorisation),
        withLatestFrom(this.clientSelector.clientAuthorizations$),
        switchMap(([action, authorizations]) => {
            const oldValues = authorizations.find(r => r.id === action.authorization.id);
            const patchValues: PatchObject[] = [];
            Object.keys(action.authorization).forEach(key => {
                if (action.authorization[key] !== oldValues[key] && !(!action.authorization[key] && !oldValues[key])) {
                    patchValues.push({value: action.authorization[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.autorisationService.updateAuthorization(patchValues, action.authorization.id).pipe(
                map(() => clientActions.updateAutorisationSuccess({ authorization: action.authorization })),
                catchError((err) => throwError(err))
            );
        })
    ));

    DeleteAutorisation$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.deleteAutorisation),
        switchMap((action) => this.clientService.deleteAuthorization(action.id).pipe(
            map(() => clientActions.deleteAutorisationSuccess({ id: action.id })),
            catchError((err) => throwError(err))
        ))
    ));

    CreateRelation$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.createRelation),
        withLatestFrom(this.clientSelector.clientKid$),
        switchMap(([action, kid]) => this.clientService.createRelation(action.relation, kid).pipe(
            map((relation) => clientActions.createRelationSuccess({relation})),
            catchError((err) => throwError(err))
        ))
    ));

    UpdateRelation$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateRelation),
        withLatestFrom(this.clientSelector.clientRelations$),
        switchMap(([action, relations]) => {
            const oldValues = relations.find(r => r.id === action.relation.id);
            const patchValues: PatchObject[] = [];
            Object.keys(action.relation).forEach(key => {
                if (action.relation[key] !== oldValues[key] && !(!action.relation[key] && !oldValues[key])) {
                    patchValues.push({value: action.relation[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.clientService.updateRelation(patchValues, action.relation.id).pipe(
                map(() => clientActions.updateRelationSuccess({ relation: action.relation })),
                catchError((err) => throwError(err))
            );
        })
    ));

    DeleteRelation$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.deleteRelation),
        switchMap((action) => this.clientService.deleteRelation(action.id).pipe(
            map(() => clientActions.deleteRelationSuccess({ id: action.id })),
            catchError((err) => throwError(err))
        ))
    ));

    CreateVederlag$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.createVederlag),
        withLatestFrom(
            this.clientSelector.client$),
        switchMap(([action, client]) => {
            const newVederlag: Vederlag = {
                ...action.vederlag,
                kid: client.kid,
                kontor: client.kontor
            };
            return this.vederlagService.createVederlag(newVederlag).pipe(
                map(vederlag => clientActions.createVederlagSuccess({vederlag}))
            );
        })
    ));

    UpdateVederlag$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateVederlag),
        withLatestFrom(this.clientSelector.clientVederlag$),
        switchMap(([action, vederlags]) => {
            const oldValues = vederlags.find(r => r.id === action.vederlag.id);
            const patchValues: PatchObject[] = [];
            Object.keys(action.vederlag).forEach(key => {
                if (action.vederlag[key] !== oldValues[key] && !(!action.vederlag[key] && !oldValues[key])) {
                    patchValues.push({value: action.vederlag[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.vederlagService.updateVederlag(patchValues, action.vederlag.id).pipe(
                map(() => clientActions.updateVederlagSuccess({ vederlag: action.vederlag })),
                catchError((err) => throwError(err))
            );
        })
    ));

    createClientAddress$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.createClientAddress),
        withLatestFrom(
            this.clientSelector.client$),
        switchMap(([action, client]) => {
            const newKunderAdr: KunderAdr = {
                ...action.kunderAdr,
                kid: client.kid,
                kontor: client.kontor
            };
            return this.kunderAdrService.createKunderAdr(newKunderAdr).pipe(
                map(kunderAdr => clientActions.createClientAddressSuccess({kunderAdr}))
            );
        })
    ));

    updateClientAddress$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.updateClientAddress),
        withLatestFrom(this.clientSelector.adresser$),
        switchMap(([action, adresser]) => {
            const oldValues = adresser.find(a => a.aid === action.kunderAdr.aid);
            const patchValues: PatchObject[] = [];
            Object.keys(action.kunderAdr).forEach(key => {
                if (action.kunderAdr[key] !== oldValues[key] && !(!action.kunderAdr[key] && !oldValues[key])) {
                    patchValues.push({value: action.kunderAdr[key], path: '/' + key, op: 'replace'});
                }
            });
            return this.kunderAdrService.updateKunderAdr(patchValues, action.kunderAdr.aid).pipe(
                map(() => clientActions.updateClientAddressSuccess({ kunderAdr: action.kunderAdr })),
                catchError((err) => throwError(err))
            );
        })
    ));

    deleteClientAddress$ = createEffect(() => this.actions$.pipe(
        ofType(clientActions.deleteClientAddress),
        switchMap((action) => this.kunderAdrService.deleteKunderAdr(action.aid).pipe(
            map(() => clientActions.deleteClientAddressSuccess({ aid: action.aid })),
            catchError((err) => throwError(err))
        ))
    ));

    constructor(
        private actions$: Actions,
        private sessionSelector: SessionSelectors,
        private clientSelector: ClientSelectors,
        private clientService: ClientService,
        private autorisationService: CreaditAutorisationService,
        private vederlagService: VederlagService,
        private kunderAdrService: KunderAdrService,
        private codeService: CreaditCodeService,
        private alert: AlertService
    ) {}

}
